import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaPhone, FaCreditCard, FaTrophy, FaCheck, FaSpinner, FaShare, FaWhatsapp, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { operators } from '../../data/operators';
import apiService from '../../services/api';
import './Modal.css';

const ParticipationModal = ({ isOpen, onClose, match, onSuccess, language = 'fr' }) => {
    const [formData, setFormData] = useState({
        homeScore: '',
        awayScore: '',
        phoneNumber: '',
        operator: '',
        country: 'CM', // Cameroun par défaut
        email: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errors, setErrors] = useState({});
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [transactionId, setTransactionId] = useState(null);
    const [participationFee, setParticipationFee] = useState(null);
    const [participationId, setParticipationId] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setFormData({
                homeScore: '',
                awayScore: '',
                phoneNumber: '',
                operator: '',
                country: 'CM', // Cameroun par défaut
                email: ''
            });
            setErrors({});
            setIsSuccess(false);
            setPaymentStatus(null);
            setTransactionId(null);
            
            // Récupérer les frais de participation
            fetchParticipationFee();
        }
    }, [isOpen, match]);

    // Récupérer les frais de participation depuis le backend
    const fetchParticipationFee = async () => {
        try {
            // Utiliser l'opérateur sélectionné par l'utilisateur, ou 'OM' par défaut si vide
            const operator = formData.operator || 'OM';
            const response = await apiService.getParticipationFee(formData.country, operator);
            if (response.success) {
                setParticipationFee(response.data);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des frais:', error);
            // Fallback vers 200 XAF (nouveaux frais)
            setParticipationFee({ amount: 50, currency: 'XAF' });
        }
    };

    // Polling pour vérifier le statut du paiement
    useEffect(() => {
        let interval;
        if (participationId && paymentStatus === 'pending') {
            interval = setInterval(async () => {
                try {
                    const response = await apiService.getParticipationStatus(participationId);
                    if (response.success && response.data.paymentStatus !== 'pending') {
                        setPaymentStatus(response.data.paymentStatus);
                        if (response.data.paymentStatus === 'completed') {
                            setIsSuccess(true);
                            toast.success(language === 'fr' ? 'Paiement réussi ! Participation validée !' : 'Payment successful! Participation validated!');
                            if (onSuccess) {
                                onSuccess();
                            }
                        } else if (response.data.paymentStatus === 'failed') {
                            toast.error(language === 'fr' ? 'Paiement échoué. Veuillez réessayer.' : 'Payment failed. Please try again.');
                            setIsSubmitting(false);
                        }
                    }
                } catch (error) {
                    console.error('Erreur lors de la vérification du statut:', error);
                }
            }, 3000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [participationId, paymentStatus, onSuccess, language]);

    const validateForm = () => {
        const newErrors = {};

        // Validation du score
        if (!formData.homeScore || !formData.awayScore) {
            newErrors.score = language === 'fr' ? 'Veuillez entrer un score complet' : 'Please enter a complete score';
        } else if (isNaN(formData.homeScore) || isNaN(formData.awayScore)) {
            newErrors.score = language === 'fr' ? 'Les scores doivent être des nombres' : 'Scores must be numbers';
        } else if (parseInt(formData.homeScore) < 0 || parseInt(formData.awayScore) < 0) {
            newErrors.score = language === 'fr' ? 'Les scores ne peuvent pas être négatifs' : 'Scores cannot be negative';
        }

        // Validation du numéro de téléphone (validation basique)
        if (!formData.phoneNumber) {
            newErrors.phoneNumber = language === 'fr' ? 'Veuillez entrer votre numéro de téléphone' : 'Please enter your phone number';
        } else if (!/^[0-9\s]+$/.test(formData.phoneNumber)) {
            newErrors.phoneNumber = language === 'fr' ? 'Le numéro ne doit contenir que des chiffres et espaces' : 'Number should only contain digits and spaces';
        }

        // Validation de l'opérateur
        if (!formData.operator) {
            newErrors.operator = language === 'fr' ? 'Veuillez sélectionner votre opérateur' : 'Please select your operator';
        }

        // Validation de l'email
        if (!formData.email) {
            newErrors.email = language === 'fr' ? 'Veuillez entrer votre email' : 'Please enter your email';
        } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
            newErrors.email = language === 'fr' ? 'Format d\'email invalide' : 'Invalid email format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const formatPhoneNumber = (value) => {
        // Nettoyer le numéro (garder seulement les chiffres et espaces)
        let cleaned = value.replace(/[^0-9\s]/g, '');
        
        // Limiter à 12 caractères (10 chiffres + 2 espaces max)
        cleaned = cleaned.substring(0, 12);
        
        return cleaned;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setFormData(prev => ({ ...prev, phoneNumber: formatted }));
    };

    const handleOperatorChange = (e) => {
        const operator = e.target.value;
        setFormData(prev => ({ 
            ...prev, 
            operator
            // Ne pas reset le numéro quand l'opérateur change
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error(language === 'fr' ? 'Veuillez corriger les erreurs dans le formulaire' : 'Please correct the errors in the form');
            return;
        }

        setIsSubmitting(true);
        setPaymentStatus('pending');

        // Google Analytics event: validate participation
        if (window.gtag) {
            window.gtag('event', 'validate_participation', {
                match_id: match.id,
                operator: formData.operator,
                home_score: formData.homeScore,
                away_score: formData.awayScore,
                fee_amount: participationFee?.amount,
                fee_currency: participationFee?.currency,
                country: formData.country
            });
        }

        try {
            const prediction = `${formData.homeScore}-${formData.awayScore}`;
            
            const response = await apiService.participate(
                match.id,
                prediction,
                formData.phoneNumber.replace(/\s/g, ''), // Enlever les espaces pour le backend
                formData.operator,
                formData.country,
                formData.email
            );

            if (response.success) {
                setParticipationId(response.data.participationId);
                setTransactionId(response.data.transactionId);
                toast.success(language === 'fr' ? 'Paiement NOKASH initié ! Vérifiez votre téléphone.' : 'NOKASH payment initiated! Check your phone.');
                
                // Le statut sera mis à jour via le polling
            } else {
                throw new Error(response.message || 'Erreur lors de l\'enregistrement');
            }
        } catch (error) {
            console.error('Erreur lors de la participation:', error);
            setPaymentStatus('failed');
            
            // Gestion des erreurs spécifiques
            if (error.message.includes('déjà participé')) {
                toast.error(language === 'fr' ? 'Tu as déjà participé à ce match !' : 'You have already participated in this match!');
            } else if (error.message.includes('fermées 10 minutes')) {
                toast.error(language === 'fr' ? 'Les participations sont fermées 10 minutes avant le match' : 'Participations are closed 10 minutes before the match');
            } else if (error.message.includes('plus ouvert')) {
                toast.error(language === 'fr' ? 'Ce match n\'est plus ouvert aux participations' : 'This match is no longer open for participation');
            } else if (error.message.includes('paiement')) {
                toast.error(language === 'fr' ? 'Erreur lors de l\'initiation du paiement NOKASH. Veuillez réessayer.' : 'Error initiating NOKASH payment. Please try again.');
            } else {
                toast.error(language === 'fr' ? 'Erreur lors de l\'enregistrement du pronostic' : 'Error registering prediction');
            }
        } finally {
            if (paymentStatus !== 'pending') {
                setIsSubmitting(false);
            }
        }
    };

    const handleShare = async (platform) => {
        // Google Analytics event: share participation
        if (window.gtag) {
            window.gtag('event', 'share_participation', {
                platform,
                match_id: match.id,
                operator: formData.operator
            });
        }
        const message = language === 'fr' 
            ? `J'ai participé au défi GigaScore ! Prédisez le score et gagnez 1 Go d'internet ! ⚽📱`
            : `I participated in the GigaScore challenge! Predict the score and win 1 GB of internet! ⚽📱`;
        const url = window.location.href;

        try {
            if (platform === 'whatsapp') {
                window.open(`https://wa.me/?text=${encodeURIComponent(message + ' ' + url)}`);
            } else if (platform === 'copy') {
                await navigator.clipboard.writeText(message + ' ' + url);
                toast.success(language === 'fr' ? 'Lien copié dans le presse-papiers !' : 'Link copied to clipboard!');
            }
        } catch (error) {
            toast.error(language === 'fr' ? 'Erreur lors du partage' : 'Error sharing');
        }
    };

    // Message de confirmation UX pro (reformulé)
    const ConfirmationMessage = ({ gigasOffered = 10 }) => (
        <div style={{ fontSize: '1.1em', lineHeight: 1.7, marginTop: 16 }}>
            <span role="img" aria-label="Bravo">🎉</span> <b>Bravo ! Ta participation est enregistrée.</b><br />
            Si ta prédiction est correcte, tu recevras <strong>{gigasOffered} Go d’internet mobile</strong> sous 24h.<br />
            Tu recevras également un <strong>SMS de confirmation</strong> automatiquement après la validation du résultat.<br />
            <span style={{ color: '#1565C0' }}>Un email de confirmation vient de t’être envoyé.</span>
        </div>
    );

    if (!isOpen || !match) return null;

    return (
        <AnimatePresence>
            <motion.div
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className="participation-modal"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button className="modal-close" onClick={onClose}>
                        <FaTimes />
                    </button>

                    {!isSuccess ? (
                        <>
                            {/* En-tête du match */}
                            <div className="match-summary">
                                <div className="match-teams">
                                    <div className="team">
                                        <img 
                                            src={match.homeTeam?.crest || '/images/placeholder_team.svg'} 
                                            alt={match.homeTeam?.name}
                                            onError={(e) => e.target.src = '/images/placeholder_team.svg'}
                                        />
                                        <span>{match.homeTeam?.name}</span>
                                    </div>
                                    <div className="vs">VS</div>
                                    <div className="team">
                                        <img 
                                            src={match.awayTeam?.crest || '/images/placeholder_team.svg'} 
                                            alt={match.awayTeam?.name}
                                            onError={(e) => e.target.src = '/images/placeholder_team.svg'}
                                        />
                                        <span>{match.awayTeam?.name}</span>
                                    </div>
                                </div>
                                <div className="match-info">
                                    <span className="competition">{match.competition?.name}</span>
                                    <span className="time">
                                        {new Date(match.scheduledTime).toLocaleString(
                                            language === 'fr' ? 'fr-FR' : 'en-US',
                                            { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Formulaire de participation */}
                            <form onSubmit={handleSubmit} className="participation-form">
                                <h3>
                                    {language === 'fr' ? 'Prédisez le score exact' : 'Predict the exact score'}
                                </h3>
                                
                                {/* Prédiction du score */}
                                <div className="score-prediction">
                                    <div className="score-inputs">
                                        <input
                                            type="number"
                                            min="0"
                                            max="20"
                                            value={formData.homeScore}
                                            onChange={(e) => setFormData(prev => ({ ...prev, homeScore: e.target.value }))}
                                            placeholder="0"
                                            className={errors.score ? 'error' : ''}
                                            disabled={isSubmitting}
                                        />
                                        <span className="score-separator">-</span>
                                        <input
                                            type="number"
                                            min="0"
                                            max="20"
                                            value={formData.awayScore}
                                            onChange={(e) => setFormData(prev => ({ ...prev, awayScore: e.target.value }))}
                                            placeholder="0"
                                            className={errors.score ? 'error' : ''}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    {errors.score && <span className="error-message">{errors.score}</span>}
                                </div>

                                {/* Sélecteur d'opérateur */}
                                <div className="form-group">
                                    <label>
                                        <FaCreditCard className="input-icon" />
                                        {language === 'fr' ? 'Opérateur Mobile Money' : 'Mobile Money Operator'}
                                    </label>
                                    <select 
                                        value={formData.operator} 
                                        onChange={handleOperatorChange}
                                        className={errors.operator ? 'error' : ''}
                                        disabled={isSubmitting}
                                    >
                                        <option value="">
                                            {language === 'fr' ? 'Sélectionnez votre opérateur' : 'Select your operator'}
                                        </option>
                                        {operators['CM']?.map(op => (
                                            <option key={op.code} value={op.code}>
                                                {op.name}
                                        </option>
                                        ))}
                                    </select>
                                    {errors.operator && <span className="error-message">{errors.operator}</span>}
                                </div>

                                {/* Numéro de téléphone */}
                                <div className="form-group">
                                    <label>
                                        <FaPhone className="input-icon" />
                                        {language === 'fr' ? 'Numéro Mobile Money' : 'Mobile Money Number'}
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phoneNumber}
                                        onChange={handlePhoneChange}
                                        placeholder={language === 'fr' ? '69 12 34 56 78' : '69 12 34 56 78'}
                                        className={errors.phoneNumber ? 'error' : ''}
                                        disabled={isSubmitting}
                                    />
                                    {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
                                </div>

                                {/* Champ email */}
                                <div className="form-group">
                                    <label>
                                        {language === 'fr' ? 'Adresse email' : 'Email address'}
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder={language === 'fr' ? 'votre@email.com' : 'your@email.com'}
                                        className={errors.email ? 'error' : ''}
                                        disabled={isSubmitting}
                                    />
                                    {errors.email && <span className="error-message">{errors.email}</span>}
                                </div>

                                {/* Message récapitulatif */}
                                <div className="participation-summary">
                                    <FaTrophy className="summary-icon" />
                                    <p>
                                        {language === 'fr' 
                                            ? 'Tu participes pour 1 Go de data internet'
                                            : 'You participate for 1 GB of internet data'}
                                    </p>
                                </div>

                                {/* Statut du paiement NOKASH */}
                                {paymentStatus === 'pending' && (
                                    <div className="payment-status pending">
                                        <FaSpinner className="spinning" />
                                        <p>
                                            {language === 'fr' 
                                                ? 'Paiement NOKASH en cours... Vérifiez votre téléphone'
                                                : 'NOKASH payment in progress... Check your phone'}
                                        </p>
                                    </div>
                                )}

                                {paymentStatus === 'failed' && (
                                    <div className="payment-status failed">
                                        <FaExclamationTriangle />
                                        <p>
                                            {language === 'fr' 
                                                ? 'Paiement NOKASH échoué. Veuillez réessayer.'
                                                : 'NOKASH payment failed. Please try again.'}
                                        </p>
                                    </div>
                                )}

                                {/* Bouton de paiement */}
                                <button 
                                    type="submit" 
                                    className="btn btn-primary"
                                    disabled={isSubmitting || paymentStatus === 'pending'}
                                >
                                    {isSubmitting || paymentStatus === 'pending' ? (
                                        <span><FaSpinner className="spin" /> {language === 'fr' ? 'Paiement en cours...' : 'Processing...'}</span>
                                    ) : (
                                        language === 'fr'
                                            ? `Valider ma participation (${participationFee?.amount || 50} ${participationFee?.currency || 'XAF'})`
                                            : `Validate my participation (${participationFee?.amount || 50} ${participationFee?.currency || 'XAF'})`
                                    )}
                                </button>

                                <p className="reassurance">
                                    {language === 'fr' 
                                        ? '💡 Zéro pari d\'argent, 100% fun et data mobile à recevoir !'
                                        : '💡 Zero money betting, 100% fun and mobile data to receive!'}
                                </p>
                            </form>
                        </>
                    ) : (
                        /* Confirmation de succès */
                        <div className="success-content">
                            <motion.div
                                className="success-icon"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                            >
                                <FaCheck />
                            </motion.div>
                            
                            <h3>
                                {language === 'fr' ? 'Bravo ! Ton pronostic est enregistré' : 'Congratulations! Your prediction is registered'}
                            </h3>
                            
                            {isSuccess && (
                                <ConfirmationMessage gigasOffered={participationFee?.gigasOffered || 1} />
                            )}

                            <div className="share-section">
                                <p>
                                    {language === 'fr' ? 'Partage ce défi avec tes amis !' : 'Share this challenge with your friends!'}
                                </p>
                                <div className="share-buttons">
                                    <button 
                                        onClick={() => handleShare('whatsapp')}
                                        className="share-button whatsapp"
                                    >
                                        <FaWhatsapp />
                                        WhatsApp
                                    </button>
                                    <button 
                                        onClick={() => handleShare('copy')}
                                        className="share-button copy"
                                    >
                                        <FaShare />
                                        {language === 'fr' ? 'Copier le lien' : 'Copy link'}
                                    </button>
                                </div>
                            </div>

                            {isSuccess && participationId && (
                                <a
                                    href={
                                        process.env.REACT_APP_API_URL
                                            ? `${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}/participate/${participationId}/receipt`
                                            : `/api/participate/${participationId}/receipt`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-success"
                                    style={{ marginTop: 16 }}
                                    onClick={() => {
                                        if (window.gtag) {
                                            window.gtag('event', 'download_receipt', {
                                                participation_id: participationId,
                                                match_id: match.id,
                                                operator: formData.operator
                                            });
                                        }
                                    }}
                                >
                                    {language === 'fr' ? 'Télécharger mon reçu' : 'Download my receipt'}
                                </a>
                            )}

                            <button onClick={onClose} className="close-button">
                                {language === 'fr' ? 'Continuer à jouer' : 'Continue playing'}
                            </button>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

ParticipationModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    onSuccess: PropTypes.func,
    language: PropTypes.string
};

export default ParticipationModal; 